import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, QuoteIcon, XIcon } from 'lucide-react';
import { campaignStages, type CampaignStage } from '../../data/campaign';
interface StageOverlayProps {
  stage: CampaignStage | null;
  preview: React.ReactNode;
  onClose: () => void;
  onNext: (stage: CampaignStage) => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function StageOverlay({
  stage,
  preview,
  onClose,
  onNext
}: StageOverlayProps) {
  const currentIndex = stage ? campaignStages.findIndex(s => s.id === stage.id) : 0;
  const nextStage = campaignStages[(currentIndex + 1) % campaignStages.length];
  useEffect(() => {
    if (!stage) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, onClose]);
  return <AnimatePresence data-id="element-323" data-mp="1bicqgz6kc5zu">
      {stage && <motion.div key="overlay" className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-324" data-mp="1zuzi7uzl0nv6">
          <div className="absolute inset-0 bg-navy-950/40 backdrop-blur-md" onClick={onClose} data-id="element-325" data-mp="1q67q9muiu3k9" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="stage-title" initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} transition={{
        duration: 0.5,
        ease
      }} className="relative flex max-h-full w-full max-w-6xl overflow-hidden rounded-[36px] bg-white shadow-2xl" data-id="element-326" data-mp="1l7o35j46ylqb">
            <aside className="relative hidden w-[38%] shrink-0 flex-col items-center justify-center overflow-hidden p-8 lg:flex" style={{
          backgroundColor: `${stage.accent}12`
        }} data-id="element-327" data-mp="xhaaprhp2kv7">
              <span className="pointer-events-none absolute -left-6 -top-10 font-serif text-[220px] leading-none opacity-[0.08]" style={{
            color: stage.accent
          }} aria-hidden="true" data-id="element-328" data-mp="3goo43lnad08o">
                {stage.number}
              </span>
              <motion.div key={stage.id} className="relative" initial={{
            opacity: 0,
            y: 24,
            scale: 0.94
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease
          }} data-id="element-329" data-mp="3bnqlj7yd646n">
                {preview}
              </motion.div>
              <p className="relative mt-5 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-navy-700" data-id="element-330" data-mp="2nwuqm1ym2sv6">
                <span className="relative flex h-2 w-2" data-id="element-331" data-mp="2e09iv8v7j3nn">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{
                backgroundColor: stage.accent
              }} data-id="element-332" data-mp="2e2unu6qsxzpm" />
                  <span className="relative inline-flex h-2 w-2 rounded-full" style={{
                backgroundColor: stage.accent
              }} data-id="element-333" data-mp="1zfvz4jjbxjel" />
                </span>
                Playing the first two screens
              </p>
              <p className="relative mt-2 text-center text-[11px] uppercase tracking-[0.22em] text-slate-500" data-id="element-334" data-mp="299w2ogyyqgl7">
                {stage.channels.join(' · ')}
              </p>
            </aside>

            <div className="flex min-w-0 flex-1 flex-col" data-id="element-335" data-mp="2iakfj8cmeawi">
              <div className="flex items-start justify-between gap-4 px-8 pt-8 md:px-10" data-id="element-336" data-mp="2eak1vpxci3qi">
                <div data-id="element-337" data-mp="2s1osy3mndnp8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em]" style={{
                color: stage.accent
              }} data-id="element-338" data-mp="2s1ykhvncc2x1">
                    {stage.number} · {stage.moment} · {stage.verbs}
                  </p>
                  <h2 id="stage-title" className="mt-2 font-serif text-[36px] leading-[1.02] text-navy-900" data-id="element-339" data-mp="31yti8cbshza9">
                    {stage.name}
                  </h2>
                  <p className="mt-2 text-[18px] font-medium text-navy-700" data-id="element-340" data-mp="ue4ej9jvlk4g">{stage.tagline}</p>
                </div>
                <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-navy-900/10 text-navy-700 transition hover:bg-mist-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-341" data-mp="zco6bql5hq4q">
                  <XIcon className="h-4 w-4" data-id="element-342" data-mp="ggfziichp77b" />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-6 pt-5 md:px-10" data-id="element-343" data-mp="ldg2ulhyfvuq">
                <p className="max-w-2xl text-[15px] leading-relaxed text-slate-600" data-id="element-344" data-mp="qe3un9e0cgq0">{stage.intro}</p>

                <figure className="mt-6 flex gap-3 rounded-2xl p-5 text-white" style={{
              backgroundColor: '#0F2442'
            }} data-id="element-345" data-mp="1wufudewha6np">
                  <QuoteIcon className="h-5 w-5 shrink-0" style={{
                color: stage.accent
              }} data-id="element-346" data-mp="3mas4eeln50bf" />
                  <blockquote className="text-[18px] font-semibold leading-snug" data-id="element-347" data-mp="3mcl6dglb6wd3">{stage.quote}</blockquote>
                </figure>

                <div className="mt-7 grid gap-x-8 gap-y-6 md:grid-cols-2" data-id="element-348" data-mp="3ck6zk9fdizk2">
                  {stage.sections.map(s => <section key={s.title} data-id="element-349" data-mp="37l2nnb12enq1">
                      <h3 className="text-[12px] font-semibold uppercase tracking-[0.16em] text-navy-900" data-id="element-350" data-mp="32mswdbigo79p">{s.title}</h3>
                      {s.body && <p className="mt-2 text-[14px] leading-relaxed text-slate-600" data-id="element-351" data-mp="2ykxsq0d25hsr">{s.body}</p>}
                      {s.bullets && <ul className="mt-2 space-y-1.5" data-id="element-352" data-mp="2tmcsi0xt2y4b">
                          {s.bullets.map(b => <li key={b} className="flex gap-2 text-[14px] leading-snug text-slate-600" data-id="element-353" data-mp="2op4ca5w7x45p">
                              <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{
                      color: stage.accent
                    }} data-id="element-354" data-mp="rsdkppjw6ywd" />
                              {b}
                            </li>)}
                        </ul>}
                    </section>)}
                </div>

                {stage.channelGroups && <section className="mt-7" data-id="element-355" data-mp="27f8nojjwqgyh">
                    <h3 className="text-[12px] font-semibold uppercase tracking-[0.16em] text-navy-900" data-id="element-356" data-mp="2ccim2op9y2yg">How it is activated</h3>
                    <div className="mt-3 grid gap-3 md:grid-cols-2" data-id="element-357" data-mp="303edvltqcy5p">
                      {stage.channelGroups.map(g => <div key={g.label} className="rounded-2xl border border-navy-900/[0.08] p-4" data-id="element-358" data-mp="350o8dfvz800e">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]" style={{
                    color: stage.accent
                  }} data-id="element-359" data-mp="2q3x4ouf9s24z">
                            {g.label}
                          </p>
                          <ul className="mt-2 space-y-1" data-id="element-360" data-mp="1enp2iu3r2stu">
                            {g.items.map(it => <li key={it} className="text-[13px] text-slate-600" data-id="element-361" data-mp="1jm0ebadlvdep">
                                {it}
                              </li>)}
                          </ul>
                        </div>)}
                    </div>
                  </section>}

                {stage.feature && <section className="mt-7 rounded-2xl p-5" style={{
              backgroundColor: `${stage.accent}10`
            }} data-id="element-362" data-mp="6q2qu5rjcawj">
                    <h3 className="text-[15px] font-semibold text-navy-900" data-id="element-363" data-mp="3f5mgyvwwtd7d">{stage.feature.title}</h3>
                    <p className="mt-1.5 text-[14px] leading-relaxed text-slate-600" data-id="element-364" data-mp="3a46vj2qtl4og">{stage.feature.body}</p>
                    {stage.feature.steps && <ol className="mt-4 grid gap-2 md:grid-cols-4" data-id="element-365" data-mp="3j6pzxra97pct">
                        {stage.feature.steps.map((st, i) => <li key={st} className="rounded-xl bg-white p-3 text-[12px] leading-snug text-navy-800" data-id="element-366" data-mp="2vfkjb9whot1g" data-mpi={i}>
                            <span className="block font-serif text-[18px]" style={{
                    color: stage.accent
                  }} data-id="element-367" data-mp="2rdz95wbcasuy">
                              0{i + 1}
                            </span>
                            {st}
                          </li>)}
                      </ol>}
                  </section>}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-navy-900/[0.06] px-8 py-4 md:px-10" data-id="element-368" data-mp="3ubni4nanb1vu">
                <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-[13px] text-slate-500 transition hover:text-navy-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-369" data-mp="835n9k6xuv4s">
                  Back to the campaign
                </button>
                <button type="button" onClick={() => onNext(nextStage)} className="flex h-11 items-center gap-2 rounded-full border border-navy-900/10 px-5 text-[13px] font-semibold text-navy-900 transition hover:bg-mist-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-iris" data-id="element-370" data-mp="lw3t60h0c5ml">
                  Next: {nextStage.name}
                  <ArrowRightIcon className="h-4 w-4" style={{
                color: nextStage.accent
              }} data-id="element-371" data-mp="qzo2e8ir3mha" />
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}